/**
 * Reclaim · Daily Essentials cross-app tracker.
 *
 * Call `markDailyEssentialDone(key)` from any page whenever the user
 * genuinely completes one of the seven Daily Essentials steps in the
 * wild — e.g. plays an audio from PrivateSuite, finishes an Atelier
 * immersion, opens a reset. The Rhythm Map on /daily-essentials will
 * light up automatically the next time they visit.
 *
 * Non-blocking + fail-silent — never breaks the parent flow.
 *
 * Valid keys (mirror backend routes/daily_essentials.py::ITEMS):
 *   morning_gratitude · morning_activation · full_morning_routine
 *   daily_immersion  · morning_intention · daily_audio
 *   daily_resets     · evening_reflection
 *
 * Session-scoped dedupe: we only fire once per key per browser session
 * so a user replaying an audio 5× in a row doesn't hammer the endpoint.
 * (The backend is idempotent anyway via $addToSet — this is a courtesy.)
 */

const API = process.env.REACT_APP_BACKEND_URL;

// Guard against double-firing within a single tab session.
const _firedThisSession = new Set();

export async function markDailyEssentialDone(key) {
  if (!key) return;
  const sessionKey = `${new Date().toISOString().slice(0, 10)}:${key}`;
  if (_firedThisSession.has(sessionKey)) return;
  _firedThisSession.add(sessionKey);

  const token = localStorage.getItem("reclaim_token");
  if (!token) return;

  try {
    await fetch(`${API}/api/daily-essentials/complete`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ key }),
      cache: "no-store",
      keepalive: true, // survive page transitions
    });
  } catch (_) {
    // Non-critical — the user's activity still happens.
    // Remove the dedupe so a subsequent action can retry.
    _firedThisSession.delete(sessionKey);
  }
}

// Convenience shortcuts.
export const trackAudioPlayed = () => markDailyEssentialDone("daily_audio");
export const trackImmersionPlayed = () => markDailyEssentialDone("daily_immersion");
export const trackResetOpened = () => markDailyEssentialDone("daily_resets");
export const trackMorningActivation = () => markDailyEssentialDone("morning_activation");
